// payments.repo — akses Supabase untuk Status Pembayaran.
import { supabase } from '@core/supabase';
import { fetchAllPaged, fetchAllChunked } from '@core/paging';
import { supplierMenagihCount } from '@modules/sparepart/sparepart.repo';
import { rowToTicket, fetchRounds, MONEY_COLS, type TicketRow } from '@core/ticketMap';
import { currentUser } from '@core/session';
import { pushNotif, resolveTaskNotif } from '@core/notif';
import { rp, todayISO } from '@domain/format';
import { paymentActionTotal, type PayActionCounts } from '@domain/paymentbadge';
import type { Ticket, Role } from '@core/types';

// maybeSingle(): kode memperlakukan 0-baris sbg null (SAH) → metode yg benar (single() error saat 0 baris).
// Source: postgrest-js JSDoc — "maybeSingle: zero or one row ... returns null when given 0 rows".
const outletOf = async (no: string): Promise<string | null> => {
  const { data } = await supabase().from('tickets').select('outlet').eq('no', no).maybeSingle();
  return (data as { outlet: string | null } | null)?.outlet ?? null;
};

export interface PayItem { ticket: Ticket; custNama: string; custWa: string | null; unit: string; }
export interface LogItem { status: string; catatan: string; by_user: string; at: string; }
export interface BillingPatch { biaya: number; dp: number; cara_bayar: string; jatuh_tempo: string | null; unsettle: boolean; }

type CustRel = { nama: string; wa: string | null };
const custOne = (c: CustRel | CustRel[] | null): CustRel | null => (Array.isArray(c) ? c[0] ?? null : c);

/**
 * Jumlah item pembayaran "perlu aksi" untuk peran user aktif → badge 💬 di menu Status Hutang, Piutang.
 * Count-only (head, tanpa fetch baris) + hanya query yang relevan dgn peran. RLS auto-scope cabang.
 * Finance = validasi (lunas belum-verif + refund pending) · CS = penagihan (tempo jatuh tempo lewat) · O/M/SPV = gabungan.
 */
export async function paymentActionCount(): Promise<number> {
  const role = currentUser()?.role;
  if (!role) return 0;
  const needValidasi = (['Owner', 'Manager', 'SPV', 'Admin Finance'] as Role[]).includes(role);
  const needOverdue = (['Owner', 'Manager', 'SPV', 'CS'] as Role[]).includes(role);
  if (!needValidasi && !needOverdue) return 0; // Teknisi dll: tak ada PR pembayaran
  const base = () => supabase().from('tickets').select('no', { count: 'exact', head: true })
    .eq('status', 'Sudah Diambil').eq('arsip_keu', false); // finScope
  const [verif, refundPending, overdue] = await Promise.all([
    // refund IS NULL: refund menang atas lunas di tampilan-dalam → jangan hitung ganda tiket ber-refund.
    needValidasi ? base().eq('lunas', true).eq('verified', false).is('refund', null).then((r) => r.count ?? 0) : Promise.resolve(0),
    needValidasi ? base().filter('refund->>status', 'eq', 'pending').then((r) => r.count ?? 0) : Promise.resolve(0),
    // Aproksimasi (kolom, murah): tempo lewat-jatuh-tempo belum lunas. TIDAK cek piutang>0 (biaya>dp tak bisa dibanding kolom murah di PostgREST)
    // → edge langka (DP ≥ biaya / biaya 0) bisa over-count. Badge = penanda urgensi, bukan angka presisi; aksi presisi tetap di dalam tab (gated piutang>0).
    needOverdue ? base().eq('lunas', false).is('refund', null).not('jatuh_tempo', 'is', null).lt('jatuh_tempo', todayISO()).then((r) => r.count ?? 0) : Promise.resolve(0),
  ]);
  const counts: PayActionCounts = { verif, refundPending, overdue };
  // Tagihan supplier ikut dihitung (permintaan Apo): notifikasi di lonceng mudah terkubur, jadi
  // menu ini harus menandakan sendiri bahwa ada supplier yang menunggu dibayar. Hanya untuk yang
  // memang mengurus pembayaran supplier.
  let tagihanSup = 0;
  if ((['Owner', 'Manager', 'Admin Finance'] as Role[]).includes(role)) {
    try { tagihanSup = await supplierMenagihCount(); } catch { /* badge tak boleh menggagalkan menu */ }
  }
  return paymentActionTotal(role, counts) + tagihanSup;
}

export async function listPayable(): Promise<PayItem[]> {
  const list = await fetchAllPaged<TicketRow & { customers: CustRel | CustRel[] | null }>((a, b) => supabase()
    .from('tickets').select(`${MONEY_COLS},unit,customers(nama,wa)`)
    // `status = Sudah Diambil` SAJA tidak cukup: saat klaim garansi, status tiket mundur lagi
    // (Dikerjakan/Siap Diambil) sedangkan pembayaran SESI SEBELUMNYA bisa masih menunggu verifikasi
    // Finance. Dulu tiket seperti itu lenyap dari daftar → uang yang sudah diterima tak pernah bisa
    // diverifikasi (kasus nyata: Rp 375.000). `ronde > 1` menarik kembali tiket ber-sesi-lama;
    // yang tak punya urusan uang tetap tersaring oleh perhitungan di view.
    .or('status.eq.Sudah Diambil,ronde.gt.1').eq('arsip_keu', false) // finScope: tiket tutup-buku tak muncul sbg piutang hantu
    .order('tgl_ambil_iso', { ascending: false }).order('no').range(a, b)) as unknown as (TicketRow & { customers: CustRel | CustRel[] | null })[];
  const rounds = await fetchRounds(list.map((r) => r.no));
  return list.map((r) => {
    const c = custOne(r.customers);
    return { ticket: rowToTicket(r, rounds.get(r.no) ?? []), custNama: c?.nama ?? '—', custWa: c?.wa ?? null, unit: (r as unknown as { unit?: string }).unit ?? '' };
  });
}

async function log(no: string, catatan: string): Promise<void> {
  await supabase().from('ticket_logs')
    .insert({ ticket_no: no, status: 'Pembayaran', catatan, by_user: currentUser()?.nama ?? '' });
}

/**
 * `via` = metode uang DITERIMA saat pelunasan (Tunai/Transfer/QRIS). Wajib dicatat: untuk tempo,
 * `cara_bayar` berisi "Tempo" — itu bukan cara menerima uang, jadi tanpa `via` pelunasan tak bisa
 * dicocokkan ke laci atau rekening saat tutup kasir. `lunas_at` = stempel waktu uang masuk.
 */
export async function markLunas(no: string, via?: string): Promise<void> {
  const { error } = await supabase().from('tickets')
    .update({ lunas: true, verified: false, lunas_at: new Date().toISOString(), ...(via ? { lunas_via: via } : {}) }).eq('no', no);
  if (error) throw new Error(error.message);
  await log(no, `Ditandai LUNAS oleh CS${via ? ` (via ${via})` : ''} (menunggu verifikasi Finance)`);
  await pushNotif('FINANCE', await outletOf(no), `💰 ${no} ditandai LUNAS — perlu verifikasi Finance.`, no);
}

export async function verify(no: string): Promise<void> {
  const { error } = await supabase().from('tickets').update({ verified: true, verified_at: new Date().toISOString() }).eq('no', no);
  if (error) throw new Error(error.message);
  await log(no, 'Pembayaran TERVERIFIKASI Finance');
  // Tugas Finance ("perlu verifikasi") SELESAI → hilangkan dari lonceng (tak lewat status, jadi
  // tak tertangkap `tahap`). Lalu beri tahu CS bahwa sudah terverifikasi.
  await resolveTaskNotif(no, 'FINANCE');
  await pushNotif('CS', await outletOf(no), `✔ Pembayaran ${no} TERVERIFIKASI Finance.`, no);
}

/** ticket_logs untuk daftar tiket tertentu (badge SLA verifikasi/refund). Scoped `.in` + chunk. */
export async function fetchLogsFor(nos: string[]): Promise<Map<string, LogItem[]>> {
  const map = new Map<string, LogItem[]>();
  const uniq = [...new Set(nos)];
  if (!uniq.length) return map;
  const rows = await fetchAllChunked<{ ticket_no: string; status: string; catatan: string | null; at: string }>(uniq,
    (bagian, a, b) => supabase().from('ticket_logs').select('ticket_no,status,catatan,at')
      .in('ticket_no', bagian).order('ticket_no').order('at').range(a, b));
  for (const r of rows) {
    const arr = map.get(r.ticket_no) ?? []; arr.push({ status: r.status, catatan: r.catatan ?? '', by_user: '', at: r.at }); map.set(r.ticket_no, arr);
  }
  return map;
}

export async function getHistory(no: string): Promise<LogItem[]> {
  const { data, error } = await supabase().from('ticket_logs')
    .select('status,catatan,by_user,at').eq('ticket_no', no).order('at');
  if (error) throw new Error(error.message);
  return (data ?? []) as LogItem[];
}

/** Edit tagihan (Owner/Manager): ubah biaya/dp/cara/jatuh-tempo + opsi batalkan status lunas. */
export async function editBilling(no: string, p: BillingPatch): Promise<void> {
  const fields: Record<string, unknown> = { biaya: p.biaya, dp: p.dp, cara_bayar: p.cara_bayar, jatuh_tempo: p.jatuh_tempo };
  if (p.unsettle) { fields.lunas = false; fields.verified = false; }
  const { error } = await supabase().from('tickets').update(fields).eq('no', no);
  if (error) throw new Error(error.message);
  await log(no, `Edit tagihan → biaya ${rp(p.biaya)} · DP ${rp(p.dp)} · ${p.cara_bayar}${p.unsettle ? ' · status lunas DIBATALKAN' : ''}`);
}

/** Pemilik tiket — dipakai memutuskan bonus rekomendasi saat pembayaran diverifikasi. */
export async function customerIdOf(no: string): Promise<number | null> {
  const { data, error } = await supabase().from('tickets').select('customer_id').eq('no', no).maybeSingle();
  if (error) throw new Error(error.message);
  return (data as { customer_id: number | null } | null)?.customer_id ?? null;
}
